import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { INSTITUTIONAL_TYPES } from '../utils/constants';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Cell,
} from 'recharts';
import {
  Search, TrendingUp, TrendingDown, Minus, ShieldCheck, AlertTriangle,
  Building2, ArrowUpDown, ChevronRight, Zap,
} from 'lucide-react';

export default function InstitutionalFlow() {
  const { instFlow, brokerData, settings } = useApp();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [signalFilter, setSignalFilter] = useState('ALL');

  const symbols = useMemo(() => {
    if (!instFlow) return [];
    return Object.values(instFlow)
      .filter(s => {
        if (search && !s.symbol.toUpperCase().includes(search.toUpperCase())) return false;
        if (signalFilter === 'LARGE_LOW') return s.largeLowCostSignal;
        if (signalFilter === 'MULTI_INST') return s.multiInstBuySignal;
        if (signalFilter === 'ANY_SIGNAL') return s.largeLowCostSignal || s.multiInstBuySignal;
        return true;
      })
      .sort((a, b) => b.signalStrength - a.signalStrength);
  }, [instFlow, search, signalFilter]);

  if (!brokerData.length) {
    return (
      <div className="p-6" data-testid="inst-flow-module">
        <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2 mb-6">
          <Building2 className="w-6 h-6 text-trading-blue" /> Institutional Flow
        </h1>
        <div className="bg-trading-surface border border-trading-border rounded-sm p-16 text-center">
          <Building2 className="w-12 h-12 text-trading-muted/30 mx-auto mb-4" />
          <p className="text-sm text-trading-muted">Import broker data to analyze institutional positions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="inst-flow-module">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Building2 className="w-6 h-6 text-trading-blue" /> Institutional Flow
          </h1>
          <p className="text-sm text-trading-muted mt-1">Per-broker position tracking and accumulation signals</p>
        </div>
      </div>

      {/* Signal Legend */}
      <div className="flex flex-wrap gap-3 mb-5 text-xs">
        <div className="flex items-center gap-1.5 bg-trading-green/5 border border-trading-green/20 px-2.5 py-1 rounded-sm">
          <Zap className="w-3 h-3 text-trading-green" />
          <span className="text-trading-green font-medium">LARGE LOW-COST</span>
          <span className="text-trading-muted">Institution holds big volume at low avg price</span>
        </div>
        <div className="flex items-center gap-1.5 bg-trading-blue/5 border border-trading-blue/20 px-2.5 py-1 rounded-sm">
          <Building2 className="w-3 h-3 text-trading-blue" />
          <span className="text-trading-blue font-medium">MULTI-INST BUY</span>
          <span className="text-trading-muted">3+ institutions buying consistently</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-trading-muted" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search symbol..." className="pl-9 bg-trading-bg border-trading-border text-trading-text h-8 text-sm" data-testid="inst-search" />
        </div>
        <Select value={signalFilter} onValueChange={setSignalFilter}>
          <SelectTrigger className="w-48 h-8 bg-trading-bg border-trading-border text-xs" data-testid="inst-signal-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-trading-surface border-trading-border z-[100]">
            <SelectItem value="ALL" className="text-xs">All Stocks</SelectItem>
            <SelectItem value="ANY_SIGNAL" className="text-xs">Any Signal</SelectItem>
            <SelectItem value="LARGE_LOW" className="text-xs">Large Low-Cost Position</SelectItem>
            <SelectItem value="MULTI_INST" className="text-xs">Multi-Institution Buy</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stock Cards */}
      <div className="space-y-4">
        {symbols.map(stock => (
          <StockFlowCard key={stock.symbol} stock={stock} navigate={navigate} />
        ))}
        {symbols.length === 0 && (
          <div className="bg-trading-surface border border-trading-border rounded-sm p-8 text-center text-trading-muted text-sm">
            No stocks match the current filter.
          </div>
        )}
      </div>
    </div>
  );
}

function StockFlowCard({ stock, navigate }) {
  const [expanded, setExpanded] = useState(true);

  const instPositions = stock.positions.filter(p => p.isInstitutional);
  const topAccumulators = instPositions.filter(p => p.netPosition > 0).slice(0, 10);
  const topDistributors = instPositions.filter(p => p.netPosition < 0).sort((a, b) => a.netPosition - b.netPosition).slice(0, 5);

  return (
    <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden" data-testid={`inst-card-${stock.symbol}`}>
      {/* Header */}
      <div
        className="px-4 py-3 border-b border-trading-border bg-trading-elevated/50 flex items-center justify-between cursor-pointer hover:bg-trading-elevated transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-4">
          <div>
            <span className="font-heading text-lg font-semibold text-trading-text">{stock.symbol}</span>
            <span className="text-sm font-mono text-trading-blue ml-2">PHP {stock.currentPrice?.toFixed(2)}</span>
          </div>
          {/* Signal badges */}
          <div className="flex gap-2">
            {stock.largeLowCostSignal && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono font-semibold bg-trading-green/15 text-trading-green border border-trading-green/30" data-testid={`signal-large-low-${stock.symbol}`}>
                <Zap className="w-3 h-3" /> LARGE LOW-COST
              </span>
            )}
            {stock.multiInstBuySignal && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono font-semibold bg-trading-blue/15 text-trading-blue border border-trading-blue/30" data-testid={`signal-multi-inst-${stock.symbol}`}>
                <Building2 className="w-3 h-3" /> {stock.instConsistentBuyers}+ INST BUYING
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <MetricPill label="Data Range" value={`${stock.firstDate?.slice(5) || '?'} to ${stock.lastDate?.slice(5) || '?'}`} color="text-trading-muted" isText />
          <MetricPill label="Inst Accum" value={stock.instAccumulators} color="text-trading-green" />
          <MetricPill label="Inst Dump" value={stock.instDistributors} color="text-trading-red" />
          <MetricPill label="Avg Inst Buy" value={stock.avgInstBuyPrice ? `${stock.avgInstBuyPrice.toFixed(4)}` : '—'} color={stock.avgInstBuyPrice && stock.currentPrice && stock.avgInstBuyPrice < stock.currentPrice ? 'text-trading-green' : 'text-trading-red'} isText />
          <ChevronRight className={`w-4 h-4 text-trading-muted transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </div>

      {expanded && (
        <div className="p-4">
          {/* Top Accumulators */}
          <div className="mb-4">
            <h4 className="text-[10px] uppercase tracking-widest text-trading-green font-semibold mb-2 flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3" /> Institutional Accumulators ({topAccumulators.length})
            </h4>
            {topAccumulators.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="bg-trading-elevated border-b border-trading-border">
                      <th>Broker</th>
                      <th>Type</th>
                      <th>Net Position</th>
                      <th>Buy Vol</th>
                      <th>Sell Vol</th>
                      <th>Avg Buy</th>
                      <th>vs Price</th>
                      <th>% of Flow</th>
                      <th>Trend</th>
                      <th>Weekly Flow</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topAccumulators.map(p => (
                      <BrokerRow key={p.broker} p={p} currentPrice={stock.currentPrice} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-trading-muted">No institutional accumulators found.</p>
            )}
          </div>

          {/* Top Distributors */}
          {topDistributors.length > 0 && (
            <div className="mb-4">
              <h4 className="text-[10px] uppercase tracking-widest text-trading-red font-semibold mb-2 flex items-center gap-1.5">
                <TrendingDown className="w-3 h-3" /> Institutional Distributors ({topDistributors.length})
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="bg-trading-elevated border-b border-trading-border">
                      <th>Broker</th>
                      <th>Type</th>
                      <th>Net Position</th>
                      <th>Buy Vol</th>
                      <th>Sell Vol</th>
                      <th>Avg Sell</th>
                      <th>% of Flow</th>
                      <th>Trend</th>
                      <th>Weekly Flow</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topDistributors.map(p => (
                      <BrokerRow key={p.broker} p={p} currentPrice={stock.currentPrice} isSeller />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Weekly Net Flow Chart */}
          {instPositions.length > 0 && (
            <WeeklyFlowChart positions={instPositions} symbol={stock.symbol} />
          )}

          {/* View full detail */}
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => navigate(`/stock/${stock.symbol}`)}
              className="text-xs text-trading-blue hover:text-trading-blue/80 flex items-center gap-1 transition-colors"
              data-testid={`view-stock-${stock.symbol}`}
            >
              View Full Analysis <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BrokerRow({ p, currentPrice, isSeller }) {
  const priceDiff = p.avgBuyPrice > 0 && currentPrice > 0
    ? ((currentPrice - p.avgBuyPrice) / p.avgBuyPrice * 100).toFixed(1)
    : null;

  return (
    <tr className="border-b border-trading-border/50 hover:bg-trading-elevated/30" data-testid={`broker-pos-${p.broker}`}>
      <td className="text-trading-text text-xs font-medium">{p.broker}</td>
      <td>
        <span className={`text-[10px] font-mono ${
          p.type === 'FOREIGN' ? 'text-trading-amber' :
          p.type === 'BANK' ? 'text-trading-blue' :
          p.type === 'INSTITUTIONAL' ? 'text-trading-green' :
          'text-trading-muted'
        }`}>{p.type}</span>
      </td>
      <td className={`font-mono text-xs font-semibold ${p.netPosition >= 0 ? 'text-trading-green' : 'text-trading-red'}`}>
        {p.netPosition >= 0 ? '+' : ''}{p.netPosition.toLocaleString()}
      </td>
      <td className="font-mono text-xs text-trading-green/80">{p.totalBuyVol.toLocaleString()}</td>
      <td className="font-mono text-xs text-trading-red/80">{p.totalSellVol.toLocaleString()}</td>
      <td className="font-mono text-xs">{isSeller ? p.avgSellPrice?.toFixed(4) : p.avgBuyPrice?.toFixed(4)}</td>
      {!isSeller && (
        <td>
          {priceDiff !== null && (
            <span className={`font-mono text-[10px] ${parseFloat(priceDiff) > 0 ? 'text-trading-green' : 'text-trading-red'}`}>
              {parseFloat(priceDiff) > 0 ? '+' : ''}{priceDiff}%
            </span>
          )}
        </td>
      )}
      {isSeller && <td className="font-mono text-xs text-trading-muted">{p.positionPct}%</td>}
      <td className="font-mono text-xs text-trading-muted">{!isSeller ? p.positionPct : ''}%</td>
      <td>
        <TrendBadge trend={p.trend} weeks={p.consecutiveBuyWeeks} />
      </td>
      <td>
        <MiniWeeklyBars weekly={p.weeklyNet} />
      </td>
    </tr>
  );
}

function TrendBadge({ trend, weeks }) {
  if (trend === 'ACCUMULATING') {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-mono text-trading-green">
        <TrendingUp className="w-3 h-3" /> {weeks > 0 && `${weeks}w`}
      </span>
    );
  }
  if (trend === 'DISTRIBUTING') {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-mono text-trading-red">
        <TrendingDown className="w-3 h-3" /> DUMP
      </span>
    );
  }
  return <span className="text-[10px] font-mono text-trading-muted"><Minus className="w-3 h-3 inline" /></span>;
}

function MiniWeeklyBars({ weekly }) {
  if (!weekly || !weekly.length) return <span className="text-trading-muted text-[10px]">—</span>;
  const maxAbs = Math.max(...weekly.map(w => Math.abs(w.net)), 1);
  return (
    <div className="flex items-end gap-px h-4" title={weekly.map(w => `${w.week}: ${w.net >= 0 ? '+' : ''}${w.net.toLocaleString()}`).join('\n')}>
      {weekly.slice(-6).map((w, i) => {
        const h = Math.max(2, Math.abs(w.net) / maxAbs * 16);
        return (
          <div
            key={i}
            className={`w-1.5 rounded-sm ${w.net > 0 ? 'bg-trading-green' : w.net < 0 ? 'bg-trading-red' : 'bg-trading-muted/30'}`}
            style={{ height: `${h}px`, marginTop: w.net < 0 ? 'auto' : undefined }}
          />
        );
      })}
    </div>
  );
}

function WeeklyFlowChart({ positions, symbol }) {
  // Aggregate all institutional weekly net flows
  const weekMap = {};
  positions.forEach(p => {
    (p.weeklyNet || []).forEach(w => {
      if (!weekMap[w.week]) weekMap[w.week] = { week: w.week, net: 0, buyers: 0, sellers: 0 };
      weekMap[w.week].net += w.net;
      if (w.net > 0) weekMap[w.week].buyers++;
      else if (w.net < 0) weekMap[w.week].sellers++;
    });
  });
  const chartData = Object.values(weekMap).sort((a, b) => {
    const aw = parseInt(a.week.replace('W', ''));
    const bw = parseInt(b.week.replace('W', ''));
    return aw - bw;
  });

  if (chartData.length < 2) return null;

  return (
    <div className="mt-3 bg-trading-bg/50 border border-trading-border/50 rounded-sm p-3">
      <h4 className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-2">
        Aggregate Institutional Weekly Net Flow
      </h4>
      <ResponsiveContainer width="100%" height={120}>
        <BarChart data={chartData}>
          <CartesianGrid stroke="#242C3D" strokeDasharray="3 3" />
          <XAxis dataKey="week" tick={{ fill: '#94A3B8', fontSize: 10 }} />
          <YAxis tick={{ fill: '#94A3B8', fontSize: 10 }} tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
          <Tooltip
            contentStyle={{ backgroundColor: '#10141D', border: '1px solid #242C3D', fontSize: 11 }}
            formatter={(val) => [val >= 0 ? `+${val.toLocaleString()}` : val.toLocaleString(), 'Net Shares']}
          />
          <ReferenceLine y={0} stroke="#242C3D" />
          <Bar dataKey="net" radius={[2, 2, 0, 0]}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.net >= 0 ? '#10B981' : '#EF4444'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function MetricPill({ label, value, color, isText }) {
  return (
    <div className="text-center">
      <span className={`${isText ? 'text-xs' : 'font-mono text-sm'} ${color}`}>{value}</span>
      <span className="text-[9px] text-trading-muted block">{label}</span>
    </div>
  );
}
